<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center pr-0 pl-0">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('search')"
          single-line
          hide-details
          class="max-width-300 me-auto"
        ></v-text-field>

        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('add_suggestion') }}
        </v-btn>
      </v-card-title>

      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="headers"
        :items="suggestions"
        :search="search"
        item-key="id"
        class="elevation-0 mt-4"
      >
        <!-- Localization Fields Column -->
        <template v-slot:item.title_ar="{ item }">
          <div>
            <strong>{{ item.title_ar }}</strong>
            <div class="text-muted small" v-if="item.title_en">{{ item.title_en }}</div>
          </div>
        </template>

        <template v-slot:item.question_ar="{ item }">
          <div>
            <span>{{ item.question_ar }}</span>
            <div class="text-muted small" v-if="item.question_en">{{ item.question_en }}</div>
          </div>
        </template>

        <template v-slot:item.solution_ar="{ item }">
          <div>
            <span>{{ item.solution_ar }}</span>
            <div class="text-muted small" v-if="item.solution_en">{{ item.solution_en }}</div>
          </div>
        </template>

        <!-- Status switch Column -->
        <template v-slot:item.is_active="{ item }">
          <v-switch
            v-model="item.is_active"
            dense
            color="success"
            @change="toggleActive(item)"
          ></v-switch>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-btn icon color="primary" small @click="openEditDialog(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" small @click="confirmDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Form Dialog -->
    <v-dialog v-model="formDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          {{ isEditMode ? $t('edit_suggestion') : $t('add_suggestion') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="isFormValid">
            <v-row>
              <!-- Title Arabic -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formModel.title_ar"
                  :label="$t('suggestion_title_ar')"
                  outlined
                  required
                  :rules="[v => !!v || 'Arabic title is required']"
                ></v-text-field>
              </v-col>

              <!-- Title English -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formModel.title_en"
                  :label="$t('suggestion_title_en')"
                  outlined
                ></v-text-field>
              </v-col>

              <!-- Problem Type -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formModel.problem_type"
                  :label="$t('complaint_category')"
                  outlined
                ></v-text-field>
              </v-col>

              <!-- Sort Order -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="formModel.sort_order"
                  label="الترتيب / Sort Order"
                  type="number"
                  outlined
                  min="0"
                ></v-text-field>
              </v-col>

              <!-- Question Arabic -->
              <v-col cols="12">
                <v-textarea
                  v-model="formModel.question_ar"
                  :label="$t('suggestion_question_ar')"
                  rows="3"
                  outlined
                  required
                  :rules="[v => !!v || 'Arabic question is required']"
                ></v-textarea>
              </v-col>

              <!-- Question English -->
              <v-col cols="12">
                <v-textarea
                  v-model="formModel.question_en"
                  :label="$t('suggestion_question_en')"
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>

              <!-- Solution Arabic -->
              <v-col cols="12">
                <v-textarea
                  v-model="formModel.solution_ar"
                  :label="$t('suggestion_solution_ar')"
                  rows="3"
                  outlined
                  required
                  :rules="[v => !!v || 'Arabic solution is required']"
                ></v-textarea>
              </v-col>

              <!-- Solution English -->
              <v-col cols="12">
                <v-textarea
                  v-model="formModel.solution_en"
                  :label="$t('suggestion_solution_en')"
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>

              <!-- Active Status -->
              <v-col cols="12">
                <v-switch
                  v-model="formModel.is_active"
                  :label="$t('Status')"
                  color="success"
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4 pl-4">
          <v-btn color="error" text @click="closeFormDialog">{{ $t('cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" :disabled="!isFormValid || isSubmitting" :loading="isSubmitting" @click="saveSuggestion">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ComplaintSuggestionsTab",
  data() {
    return {
      suggestions: [],
      isLoading: false,
      search: "",
      formDialog: false,
      isEditMode: false,
      isFormValid: false,
      isSubmitting: false,
      selectedId: null,
      formModel: {
        title_ar: "",
        title_en: "",
        problem_type: "",
        question_ar: "",
        question_en: "",
        solution_ar: "",
        solution_en: "",
        is_active: true,
        sort_order: 0
      },
      headers: [
        { text: this.$t('suggestion_title_ar') || 'العنوان', value: "title_ar" },
        { text: this.$t('complaint_category') || 'نوع المشكلة', value: "problem_type" },
        { text: this.$t('suggestion_question_ar') || 'السؤال', value: "question_ar" },
        { text: this.$t('suggestion_solution_ar') || 'الحل المقترح', value: "solution_ar" },
        { text: this.$t('Status') || 'الحالة', value: "is_active", sortable: false },
        { text: 'الترتيب', value: "sort_order" },
        { text: this.$t('Actions') || 'الإجراءات', value: "actions", sortable: false, align: "center" },
      ]
    };
  },
  mounted() {
    this.loadSuggestions();
  },
  methods: {
    loadSuggestions() {
      this.isLoading = true;
      axios
        .get("/driver-complaint-suggestions")
        .then(response => {
          this.suggestions = response.data;
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "Error",
            text: "Failed to load complaint suggestions",
            type: "error"
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    openCreateDialog() {
      this.isEditMode = false;
      this.selectedId = null;
      this.formModel = {
        title_ar: "",
        title_en: "",
        problem_type: "",
        question_ar: "",
        question_en: "",
        solution_ar: "",
        solution_en: "",
        is_active: true,
        sort_order: 0
      };
      this.formDialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    openEditDialog(item) {
      this.isEditMode = true;
      this.selectedId = item.id;
      this.formModel = {
        title_ar: item.title_ar,
        title_en: item.title_en || "",
        problem_type: item.problem_type || "",
        question_ar: item.question_ar,
        question_en: item.question_en || "",
        solution_ar: item.solution_ar,
        solution_en: item.solution_en || "",
        is_active: item.is_active,
        sort_order: item.sort_order || 0
      };
      this.formDialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.validate();
      });
    },
    closeFormDialog() {
      this.formDialog = false;
    },
    saveSuggestion() {
      if (!this.$refs.form.validate()) return;
      this.isSubmitting = true;

      const apiCall = this.isEditMode
        ? axios.put(`/driver-complaint-suggestions/${this.selectedId}`, this.formModel)
        : axios.post("/driver-complaint-suggestions", this.formModel);

      apiCall
        .then(() => {
          this.$notify({
            title: "Success",
            text: this.isEditMode ? "Suggestion updated successfully" : "Suggestion added successfully",
            type: "success"
          });
          this.formDialog = false;
          this.loadSuggestions();
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "Error",
            text: "Failed to save suggestion",
            type: "error"
          });
        })
        .then(() => {
          this.isSubmitting = false;
        });
    },
    toggleActive(item) {
      axios
        .put(`/driver-complaint-suggestions/${item.id}`, {
          title_ar: item.title_ar,
          title_en: item.title_en,
          problem_type: item.problem_type,
          question_ar: item.question_ar,
          question_en: item.question_en,
          solution_ar: item.solution_ar,
          solution_en: item.solution_en,
          sort_order: item.sort_order,
          is_active: item.is_active
        })
        .then(() => {
          this.$notify({
            title: "Success",
            text: "Status updated successfully",
            type: "success"
          });
        })
        .catch(error => {
          console.error(error);
          item.is_active = !item.is_active; // Revert switch if call fails
          this.$notify({
            title: "Error",
            text: "Failed to update suggestion status",
            type: "error"
          });
        });
    },
    confirmDelete(item) {
      this.$swal({
        title: this.$t('confirm_delete') || "Are you sure?",
        text: "You will not be able to recover this suggestion!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete') || "Yes, delete it!",
        cancelButtonText: this.$t('cancel') || "Cancel"
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteSuggestion(item.id);
        }
      });
    },
    deleteSuggestion(id) {
      this.isLoading = true;
      axios
        .delete(`/driver-complaint-suggestions/${id}`)
        .then(() => {
          this.$notify({
            title: "Success",
            text: "Suggestion deleted successfully",
            type: "success"
          });
          this.loadSuggestions();
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "Error",
            text: "Failed to delete suggestion",
            type: "error"
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.max-width-300 {
  max-width: 300px;
}
</style>
